/* eslint-disable no-nested-ternary */
/* eslint-disable indent */
/**
 *
 * Dropdown
 *
 */

import {
  Checkbox,
  FormControlLabel,
  ListSubheader,
  Select,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { withTheme } from 'styled-components';

import ArrowCollapseDown from 'assets/ArrowCollapseDown';
import ArrowCollapseUp from 'assets/ArrowCollapseUp';
import SearchField from 'components/v2/SearchField';
import { COLORS, newLayout } from 'theme';

import {
  MenuItemContent,
  SearchWrapper,
  StyledIconComponent,
  StyledMenuItem,
} from './Dropdown.styles';

export const ADORNMENT_PLACES = {
  LEFT: 'left',
  RIGHT: 'right',
};

export const SIZES = {
  XXS: 'xxs',
  TINY: 'tiny',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};

const renderValue = (selected, options, multiple) => {
  if (multiple) {
    return options
      .filter((option) => selected.includes(option.value))
      .map((option) => option.label)
      .join(', ');
  }
  return options
    .filter((option) => option.value === selected)
    .map((option) => option.label);
};

const buildOptions = (options) =>
  [].concat(
    ...Object.values(
      options.reduce((group, option) => {
        const { category } = option;
        // eslint-disable-next-line no-param-reassign
        group[category] = group[category] ?? [];
        group[category].push(option);
        return group;
      }, {}),
    ),
  );

const Dropdown = ({
  multiple,
  label,
  value,
  onChange,
  options,
  onSearch,
  renderOptions = null,
  hoverRenderOption,
  isBorderOutlined = true,
  scrollableOption = false,
  loadMore,
  disabled,
  valid,
  size = SIZES.TINY,
  helperText,
  adornment,
  required,
  error,
  theme,
  filterOptionsSearch,
  sx,
  markSelectedOption,
  isLoading,
  EndAdornmentIcon, // Optional status icon (e.g. "this field changed"); takes the arrow's slot
  ...props
}) => {
  const getHelperText = () => {
    if (!active && valid === false && error) {
      return error;
    }
    return helperText;
  };

  const isNonEmpty = useCallback(
    (val) =>
      multiple
        ? Array.isArray(val) && val.length > 0
        : val != null && val !== '',
    [multiple],
  );

  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const [stateValue, setStateValue] = useState(value);
  const [stateOptions, setStateOptions] = useState(buildOptions(options));
  const [searchValue, setSearchValue] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [helper, setHelper] = useState(getHelperText());
  const [isValueNonEmpty, setIsValueNonEmpty] = useState(isNonEmpty(value));

  const [hoverAnchor, setHoverAnchor] = useState(null);
  const [hoverOptionValue, setHoverOptionValue] = useState(null);

  const searchFieldRef = useRef(undefined);
  const fieldWidth = useRef();
  const menuListRef = useRef(null);

  const isLarge = size === SIZES.LARGE;
  const isMedium = size === SIZES.MEDIUM;
  const isTiny = size === SIZES.TINY;
  const isXxs = size === SIZES.XXS;

  const handleChange = (event) => {
    const targetValue = event.target.value;

    // Find if the selected option has showOnHover flag
    const selectedOption = stateOptions.find(
      (opt) => opt.value === targetValue,
    );
    if (selectedOption?.showOnHover) {
      // Don't update value for showOnHover options
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    setStateValue(targetValue);
    onChange(targetValue);
  };

  useEffect(() => {
    if (isOpen) {
      setSearchValue('');
    } else {
      setHoverAnchor(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (onSearch) {
      onSearch(searchValue);
    } else if (filterOptionsSearch) {
      const filteredOptions = buildOptions(options).filter((option) =>
        option.label?.toLowerCase()?.includes(searchValue?.toLowerCase()),
      );
      setStateOptions(filteredOptions);
    }
  }, [searchValue]);

  useEffect(() => {
    const flattenOptions = buildOptions(options);
    if (filterOptionsSearch && searchValue) {
      setStateOptions(
        flattenOptions.filter((option) =>
          option.label?.toLowerCase()?.includes(searchValue),
        ),
      );
    } else {
      setStateOptions(flattenOptions);
    }
  }, [options]);

  useEffect(() => {
    setHelper(getHelperText());
  }, [valid, helperText, error]);

  useEffect(() => {
    setStateValue(value);
    setIsValueNonEmpty(isNonEmpty(value));
    setActive(false);
    setHover(false);
  }, [value]);

  const componentColor = useMemo(() => {
    if (disabled || active) return 'primary';
    switch (valid) {
      case true:
        return 'success';
      case false:
        return 'error';
      default:
        return 'primary';
    }
  }, [disabled, valid, active]);

  const getAdornmentColor = useMemo(() => {
    let adormentColor = COLORS.black50;
    if (disabled) {
      adormentColor = COLORS.black30;
    } else if (hover || active) {
      adormentColor = COLORS.kinetikBlack;
    }
    return adormentColor;
  }, [disabled, hover, active]);

  const getIconComponent = useCallback(
    () =>
      // Only one icon at a time — a status icon takes the slot from the arrow.
      // Errors outrank it, in which case the arrow stays.
      valid !== false && EndAdornmentIcon ? (
        EndAdornmentIcon
      ) : (
        <StyledIconComponent
          onClick={() => setIsOpen((prev) => !prev)}
          disableRipple
          disabled={disabled}
        >
          {isOpen ? <ArrowCollapseUp /> : <ArrowCollapseDown />}
        </StyledIconComponent>
      ),
    [isOpen, disabled, valid, EndAdornmentIcon],
  );

  return (
    // Options can handle array and object with categories
    // Each option can handle different type of list item
    // See storybook `Dropdown` for details
    // Also see: https://github.com/mui/material-ui/issues/13898 since 2018 bug with start adornment
    <>
      <Select
        open={isOpen}
        fullWidth
        multiple={multiple}
        value={stateValue}
        onChange={handleChange}
        ref={fieldWidth}
        input={
          <TextField
            select
            helperText={helper}
            label={size !== SIZES.TINY ? label : null}
            InputLabelProps={{
              shrink: isValueNonEmpty,
              color: componentColor,
            }}
            required={required}
            error={error}
            color={componentColor}
          >
            {/* Line below need to be added because of mui TextField limitations */}
            &nbsp;
          </TextField>
        }
        renderValue={(selected) => renderValue(selected, options, multiple)}
        IconComponent={getIconComponent}
        disabled={disabled}
        onOpen={() => setIsOpen(true)}
        onClose={() => {
          setIsOpen(false);
        }}
        error={disabled ? undefined : valid === false}
        size={size}
        onFocus={() => {
          setIsValueNonEmpty(true);
          if (!disabled) {
            setActive(true);
          }
        }}
        onBlur={(e) => {
          setIsValueNonEmpty(isNonEmpty(e.target.value));
          if (!disabled) {
            setActive(false);
            setHover(false);
          }
        }}
        onMouseEnter={() => !disabled && setHover(true)}
        onMouseLeave={() => !disabled && setHover(false)}
        InputProps={{
          startAdornment: adornment,
        }}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            ref: menuListRef,
            sx: {
              maxHeight: 320,
              maxWidth: fieldWidth?.current?.clientWidth,
              borderRadius: theme.borderRadius.secondary,
              ...(scrollableOption && { overflowX: 'auto' }),
              boxShadow: '0 15px 40px rgba(0, 0, 0, 0.25)',
              '.MuiList-root': {
                padding: `${theme.spacing.s1} 0`,
                ...((onSearch || filterOptionsSearch) && {
                  paddingTop: 0,
                }),
                '.MuiButtonBase-root': {
                  margin: theme.spacing.s1,
                  borderRadius: theme.borderRadius.secondary,
                  color: COLORS.black90,
                  fontWeight: 400,
                  fontSize: theme.fontSize.body2,
                  padding: `${theme.spacing.s2} ${theme.spacing.s3}`,
                  '&:first-of-type:not(.MuiCheckbox-root)': {
                    marginTop: 0,
                  },
                  '&:last-child': {
                    marginBottom: 0,
                  },
                  '&.Mui-selected': {
                    backgroundColor: COLORS.white,
                    color: COLORS.kinetikBlack,
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: COLORS.black04,
                    },
                  },
                  '&:hover': {
                    color: COLORS.kinetikBlack,
                    backgroundColor: COLORS.black04,

                    '.MuiCheckbox-root': {
                      '+ .MuiFormControlLabel-label': {
                        color: COLORS.kinetikBlack,
                      },
                    },
                  },
                  '.MuiCheckbox-root': {
                    padding: '0 6px',
                    '+ .MuiFormControlLabel-label': {
                      fontWeight: 400,
                    },
                  },
                },
                '.MuiListSubheader-root': {
                  padding: `0 ${theme.spacing.s4}`,
                  textTransform: 'uppercase',
                  fontWeight: 400,
                  marginBottom: `-${theme.spacing.s1}`,
                },
              },
            },
          },
        }}
        onAnimationEnd={() => searchFieldRef.current?.focus()}
        sx={{
          ...(isValueNonEmpty &&
            isBorderOutlined &&
            !active &&
            valid === undefined &&
            !disabled && {
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: `${newLayout.border.filled.color} !important`,
              },
            }),
          svg: {
            marginRight: theme.spacing.s4,
            width: theme.fontSize.body4,
            path: {
              fill: getAdornmentColor,
            },
          },
          '.MuiFormLabel-root': {
            fontSize: isMedium ? theme.fontSize.body1 : theme.fontSize.body3,
            fontWeight: 500,
            left: adornment ? theme.spacing.m4 : '2px',
            ...(hover &&
              valid === undefined && {
                '&[data-shrink="false"]': {
                  color: COLORS.kinetikBlack,
                },
              }),
            ...(active && {
              '& .MuiFormLabel-asterisk': {
                color: COLORS.black30,
              },
            }),
          },
          '.MuiInputLabel-root': {
            ...(disabled && {
              '&[class*="MuiFormLabel-"]': {
                color: COLORS.black30,
              },
            }),
            transform: `translate(14px, ${theme.spacing.s4})`,
            '&.MuiInputLabel-shrink': {
              transform: `translate(14px, 8px) scale(${isMedium ? 0.75 : 1})`,
              '+ .MuiInputBase-root': {
                div: {
                  transform: `translate(${adornment ? '-1px' : '0'}, 8px)`,
                },
              },
            },
          },
          '.MuiInputBase-root': {
            background: COLORS.white,
            fieldset: {
              top: 0,
              legend: {
                position: 'absolute',
              },
            },
            fontSize: theme.fontSize.body3,
            '.MuiInputBase-input': {
              height: isLarge
                ? theme.lineHeight.h1
                : isMedium || isTiny
                  ? theme.lineHeight.body1
                  : theme.lineHeight.body3,
              lineHeight:
                isMedium || isTiny
                  ? theme.lineHeight.body1
                  : theme.lineHeight.body3,
              fontWeight: 500,
              padding: adornment
                ? `${theme.spacing.s4} 9px`
                : isLarge
                  ? `${theme.spacing.s2} ${theme.spacing.s4}`
                  : `${theme.spacing.s4} ${theme.spacing.s4}`,
              fontSize:
                isMedium || isTiny
                  ? theme.fontSize.body1
                  : theme.fontSize.body3,
              '> div': {
                fontSize:
                  isMedium || isTiny
                    ? theme.fontSize.body1
                    : theme.fontSize.body3,
              },
            },
            '.MuiSvgIcon-root': {
              width: theme.spacing.m2,
              marginRight: 0,
            },
            '&.Mui-focused:not(.Mui-disabled):not(.Mui-error), &:not(.Mui-disabled):not(.Mui-error):hover':
              {
                '+ .MuiFormHelperText-root': {
                  color: COLORS.kinetikBlack,
                },
              },
            '.MuiFormHelperText-root:not(.Mui-disabled)': {
              color: theme.text.primary,
              '&.Mui-error': {
                color: theme.text.error,
              },
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.kinetikBlue,
            },
            '.MuiSelect-select': {
              flex: 1,
              fontWeight: 500,
              overflow: 'hidden !important',
              ...(isTiny && {
                paddingTop: theme.spacing.s2,
                paddingBottom: theme.spacing.s2,
              }),
              ...(isXxs && {
                paddingTop: theme.spacing.s2,
                paddingBottom: theme.spacing.s2,
              }),
            },
            '.MuiSelect-select.Mui-disabled': {
              cursor: 'not-allowed',
            },
          },
          '.MuiFormHelperText-root': {
            textAlign: 'left',
            margin: `2px ${theme.spacing.s4}`,
          },
          ...sx,
        }}
        {...props}
      >
        {(onSearch || filterOptionsSearch) && (
          <SearchWrapper>
            <SearchField
              placeholder="Type to search"
              onSearch={(e) => setSearchValue(e.target.value)}
              value={searchValue}
              onClear={() => setSearchValue('')}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  e.stopPropagation();
                  const firstMenuItem = menuListRef.current?.firstChild;
                  firstMenuItem?.focus();
                } else if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
              inputRef={searchFieldRef}
              loading={isLoading}
            />
          </SearchWrapper>
        )}
        {!stateOptions?.length && (
          <StyledMenuItem disabled>
            <MenuItemContent adornmentPlace={ADORNMENT_PLACES.LEFT}>
              No results found
            </MenuItemContent>
          </StyledMenuItem>
        )}
        {!renderOptions &&
          stateOptions.map((option, index) => {
            const isSelected = multiple
              ? stateValue.includes(option.value)
              : stateValue === option.value;
            return [
              // Because we have flat structure we need to verify previous category
              option.category &&
                (!stateOptions[index - 1] ||
                  stateOptions[index - 1].category !== option.category) && (
                  <ListSubheader>{option.category}</ListSubheader>
                ),
              <StyledMenuItem
                $selected={markSelectedOption && isSelected}
                value={option.value}
                onMouseEnter={(e) => {
                  if (hoverRenderOption && option.showOnHover) {
                    setHoverAnchor(e.currentTarget);
                    setHoverOptionValue(option.value);
                  }
                }}
                onMouseLeave={() => {
                  if (!option.showOnHover) {
                    setHoverAnchor(null);
                    setHoverOptionValue(null);
                  }
                }}
              >
                {multiple ? (
                  <FormControlLabel
                    control={<Checkbox checked={isSelected} />}
                    label={option.label}
                    sx={{
                      pointerEvents: 'none', // Disables pointer events, so only the parent `MenuItem` will handle the click event (prevents double-triggering the click event)
                    }}
                  />
                ) : (
                  <MenuItemContent
                    adornmentPlace={
                      option.adornmentPlace ?? ADORNMENT_PLACES.LEFT
                    }
                  >
                    {option.adornment}
                    {option.label}
                  </MenuItemContent>
                )}
              </StyledMenuItem>,
            ];
          })}
        {renderOptions}
        {loadMore}
      </Select>

      {/* Hover-driven content popper */}
      {hoverRenderOption &&
        hoverAnchor &&
        hoverOptionValue &&
        hoverRenderOption({
          anchorEl: hoverAnchor,
          value: hoverOptionValue,
          onClose: () => {
            setHoverAnchor(null);
            setHoverOptionValue(null);
            setIsOpen(false);
          },
          open: Boolean(hoverAnchor),
        })}
    </>
  );
};

Dropdown.propTypes = {
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  label: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.string,
  multiple: PropTypes.bool,
  value: PropTypes.any,
  adornment: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  loadMore: PropTypes.element,
  renderOptions: PropTypes.func,
  hoverRenderOption: PropTypes.func,
  isBorderOutlined: PropTypes.bool,
  scrollableOption: PropTypes.bool,
  disabled: PropTypes.bool,
  valid: PropTypes.bool,
  required: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(SIZES)),
  theme: PropTypes.object,
  filterOptionsSearch: PropTypes.bool,
  sx: PropTypes.object,
  markSelectedOption: PropTypes.bool,
  isLoading: PropTypes.bool,
  EndAdornmentIcon: PropTypes.element,
};

export default memo(withTheme(Dropdown));
